import { CheckCircleFilled, ReloadOutlined } from '@ant-design/icons'
import { DownloadButtons } from '@components/_SiteWide/UploadStatus/DownloadButtons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { DateSkipper, GraviButton } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import { notification, Popconfirm, Space } from 'antd'
import moment from 'moment/moment'
import React, { useMemo, useState } from 'react'
import { useMetadata } from '@api/useMetadata'
import { formatters } from '@utils/intl/dateTimeFormatters'

export function ActionButtons({ weekCount, payrollRows, filters, onWeekChange }) {
  const { scopes } = useSecurityContext()
  const canApprovePayroll = scopes.driver?.payroll_report.edit
  const [excelLoading, setExcelLoading] = useState()
  const [recalcLoading, setRecalcLoading] = useState()
  const [approveAllLoading, setApproveAllLoading] = useState()
  const queryClient = useQueryClient()
  const { data: payrollMetadata } = useMetadata('payroll')
  const additionalFormats = useMemo(() => {
    return payrollMetadata?.available_download_formats?.map((format) => {
      return { name: format, title: `Download ${format}` }
    })
  }, [payrollMetadata])

  return (
    <>
      {canApprovePayroll && (
        <Space>
          <DownloadButtons
            additionalFormats={additionalFormats}
            onDownload={(downloadOption) => downloadExcelPayroll(filters?.date, setExcelLoading, downloadOption)}
            isLoading={excelLoading}
            disabled={excelLoading}
          />
          <GraviButton
            buttonText='Recalculate Payroll'
            className='ml-2'
            onClick={() => recalculateAllPayroll(filters, queryClient, setRecalcLoading)}
            loading={recalcLoading}
            icon={<ReloadOutlined />}
          />
          <Popconfirm
            onConfirm={() => approveAll(payrollRows, setApproveAllLoading, queryClient)}
            title='Are you sure? This will close the payroll for the current period.'
          >
            <GraviButton
              buttonText='Approve All'
              theme2
              loading={approveAllLoading}
              className='mr-2'
              icon={<CheckCircleFilled />}
            />
          </Popconfirm>
        </Space>
      )}
      <DateSkipper
        startDate={filters?.date}
        onChange={onWeekChange}
        daysToSkip={weekCount * 7}
        format={formatters.DATE}
      />
    </>
  )
}
const downloadExcelPayroll = (startDate, setExcelLoading, downloadOption) => {
  const date = moment(startDate).toISOString()
  const download_format = downloadOption?.name !== 'excel' ? downloadOption?.name : null
  setExcelLoading(true)
  api
    .exportPayroll(date, download_format)
    .then((resp) => {
      const url = window.URL.createObjectURL(new Blob([resp.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'payroll_export.xlsx')
      document.body.appendChild(link)
      link.click()
    })
    .finally(() => setExcelLoading(false))
}

const recalculateAllPayroll = (filters, queryClient, setRecalcLoading) => {
  const date = moment.utc(filters?.date).toISOString()
  setRecalcLoading(true)
  api
    .recalculateAllPayroll(date)
    .then(() => {
      notification.success({
        message: 'Recalculated Payroll.',
      })

      queryClient.invalidateQueries({
        queryKey: ['payrollOverviewQuery'],
      })
    })
    .catch(() => {
      notification.error({
        message: 'Error Recalculating Payroll.',
        duration: 8,
      })
    })
    .finally(() => setRecalcLoading(false))
}

const approveAll = (payrollRows, setApproveAllLoading, queryClient) => {
  if (payrollRows?.length > 0) {
    setApproveAllLoading(true)
    const validResults = payrollRows.filter((r) => !r.has_error && r.status.toLowerCase() !== 'approved')
    if (validResults.length > 0) {
      const ids = validResults.map((r) => r.key)
      api
        .approvalAllPayroll(ids)
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ['payrollOverviewQuery'],
          })
        })
        .finally(() => setApproveAllLoading(false))
    }
  }
}
